import { useState } from 'react';
import {
  App,
  Badge,
  Button,
  Card,
  Dropdown,
  Input,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableProps, MenuProps } from 'antd';
import {
  EditOutlined,
  KeyOutlined,
  LockOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
  CloudOutlined,
  TeamOutlined,
  UnlockOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useDeactivateUser,
  useReactivateUser,
  useResetPassword,
  useUnlockUser,
  useUsers,
} from '@/hooks/useUsers';
import { useAuthStore } from '@/stores/authStore';
import type { UserDto } from '@/types/user';
import { UserFormModal } from './UserFormModal';
import { UserResetPasswordModal } from './UserResetPasswordModal';
import { UserAssignEnterpriseModal } from './UserAssignEnterpriseModal';
import { UserMdmModal } from './UserMdmModal';

const { Title, Text } = Typography;

export function UsersPage() {
  const { message } = App.useApp();
  const currentUser = useAuthStore((s) => s.user);
  const isSuperAdmin = currentUser?.isSuperAdmin ?? false;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserDto | null>(null);

  const [resetPwdUser, setResetPwdUser] = useState<UserDto | null>(null);
  const [assignUser, setAssignUser] = useState<UserDto | null>(null);
  const [assignMdmUser, setAssignMdmUser] = useState<UserDto | null>(null);

  const { data, isLoading, refetch, isFetching } = useUsers({
    page,
    pageSize,
    search: search || undefined,
    includeInactive,
    sortBy: 'lastName',
  });

  const deactivate = useDeactivateUser();
  const reactivate = useReactivateUser();
  const unlock = useUnlockUser();

  const handleDeactivate = async (u: UserDto) => {
    try {
      await deactivate.mutateAsync(u.id);
      message.success(`Usuario "${u.fullName}" desactivado.`);
    } catch {
      // interceptor muestra error
    }
  };

  const handleReactivate = async (u: UserDto) => {
    try {
      await reactivate.mutateAsync(u.id);
      message.success(`Usuario "${u.fullName}" reactivado.`);
    } catch {
      // interceptor muestra error
    }
  };

  const handleUnlock = async (u: UserDto) => {
    try {
      await unlock.mutateAsync(u.id);
      message.success(`Cuenta "${u.fullName}" desbloqueada.`);
    } catch {
      // interceptor muestra error
    }
  };

  const columns: TableProps<UserDto>['columns'] = [
    {
      title: 'Usuario',
      key: 'user',
      sorter: true,
      render: (_, u) => (
        <Space direction="vertical" size={0}>
          <Space>
            <UserOutlined style={{ color: 'var(--color-primary)' }} />
            <Text strong>{u.fullName}</Text>
            {u.id === currentUser?.id && <Tag color="blue">Vos</Tag>}
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>{u.email}</Text>
        </Space>
      ),
    },
    {
      title: 'Tipo',
      key: 'type',
      width: 140,
      render: (_, u) =>
        u.isSuperAdmin ? (
          <Tag color="purple">Super Admin</Tag>
        ) : (
          <Tag color="default">Usuario</Tag>
        ),
    },
    {
      title: 'Enterprises',
      key: 'enterprises',
      render: (_, u) => {
        if (u.isSuperAdmin) {
          return <Tag color="purple">Acceso global</Tag>;
        }
        if (u.enterpriseAssignments.length === 0) {
          return <Text type="secondary" italic>Sin asignaciones</Text>;
        }
        return (
          <Space wrap size={[4, 4]}>
            {u.enterpriseAssignments.slice(0, 3).map((a) => (
              <Tooltip key={a.enterpriseId} title={`Rol: ${a.roleNameEs}`}>
                <Tag>{a.enterpriseName}</Tag>
              </Tooltip>
            ))}
            {u.enterpriseAssignments.length > 3 && (
              <Tag>+{u.enterpriseAssignments.length - 3}</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Estado',
      key: 'state',
      width: 130,
      render: (_, u) => {
        if (!u.isActive) return <Tag color="error">Inactivo</Tag>;
        if (u.isLockedOut) return <Tag color="warning" icon={<LockOutlined />}>Bloqueado</Tag>;
        return <Tag color="success">Activo</Tag>;
      },
    },
    {
      title: 'Ultimo acceso',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 160,
      render: (v) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : <Text type="secondary">Nunca</Text>,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 100,
      align: 'right',
      render: (_, u) => {
        const items: MenuProps['items'] = [
          {
            key: 'edit',
            label: 'Editar',
            icon: <EditOutlined />,
            onClick: () => {
              setEditingUser(u);
              setFormOpen(true);
            },
          },
          {
            key: 'reset-pwd',
            label: 'Resetear password',
            icon: <KeyOutlined />,
            onClick: () => setResetPwdUser(u),
          },
          {
            key: 'assign',
            label: 'Asignar Enterprise',
            icon: <TeamOutlined />,
            disabled: u.isSuperAdmin,
            onClick: () => setAssignUser(u),
          },
          {
            key: 'assign-mdm',
            label: 'Asignar MDM',
            icon: <CloudOutlined />,
            disabled: u.isSuperAdmin,
            onClick: () => setAssignMdmUser(u),
          },
          ...(u.isLockedOut ? [{
            key: 'unlock',
            label: 'Desbloquear',
            icon: <UnlockOutlined />,
            onClick: () => handleUnlock(u),
          }] : []),
          { type: 'divider' as const },
          ...(u.isActive
            ? [{
                key: 'deactivate',
                label: 'Desactivar',
                icon: <StopOutlined />,
                danger: true,
                disabled: u.id === currentUser?.id,
                onClick: () => handleDeactivate(u),
              }]
            : [{
                key: 'reactivate',
                label: 'Reactivar',
                icon: <ReloadOutlined />,
                onClick: () => handleReactivate(u),
              }]),
        ];

        return (
          <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <UserOutlined style={{ marginRight: 12 }} />
          Usuarios
        </Title>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => {
            setEditingUser(null);
            setFormOpen(true);
          }}
        >
          Nuevo Usuario
        </Button>
      </div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <Space>
            <Input
              placeholder="Buscar por nombre o email..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={() => setPage(1)}
              style={{ width: 320 }}
              allowClear
            />
            <Button onClick={() => { setPage(1); refetch(); }}>Buscar</Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
              Refrescar
            </Button>
          </Space>

          <Space>
            <Text>Incluir inactivos:</Text>
            <Switch checked={includeInactive} onChange={(v) => { setIncludeInactive(v); setPage(1); }} />
          </Space>
        </div>

        <Table<UserDto>
          rowKey="id"
          columns={columns}
          dataSource={data?.items ?? []}
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total: data?.totalCount ?? 0,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total) => `${total} en total`,
          }}
        />
      </Card>

      <UserFormModal
        open={formOpen}
        user={editingUser}
        onClose={() => {
          setFormOpen(false);
          setEditingUser(null);
        }}
      />

      <UserResetPasswordModal
        user={resetPwdUser}
        onClose={() => setResetPwdUser(null)}
      />

      <UserAssignEnterpriseModal
        user={assignUser}
        onClose={() => setAssignUser(null)}
      />

      <UserMdmModal
        user={assignMdmUser}
        onClose={() => setAssignMdmUser(null)}
      />
    </div>
  );
}
